<style scoped>
.btn-radios >>> .btn {
  cursor: pointer;
}
</style>

<template>
  <b-modal
    hide-footer
    id="finances-details-modal"
    ref="assetsDetailsModal"
    title="Assets &amp; Liabilities - Details"
    header-class="anchor-modal"
    size="xl"
  >
    <b-row>
      <b-col>
        <b-form-group class="text-center">
          <b-form-radio-group
            id="btn-radios-type"
            class="btn-radios"
            name="input-type"
            v-model="model.type"
            :options="options.type"
            buttons
          >
          </b-form-radio-group>
        </b-form-group>
      </b-col>
    </b-row>

    <filter-table
      primary-key="created"
      empty-text="No Records Found."
      ref="filterTable"
      :fields="currentFields"
      :items="currentItems"
      :slots="templates"
      :clickable="true"
      @row-clicked="itemClicked"
    >
      <template slot="header">
        <div class="col-md-6 offset-md-6">
          <b-form-select
          v-model="model.selected"
          :options="options.selected"
          >
        </b-form-select>
        </div>
      </template>

      <template
        v-for="cur in templateTypes.currency"
        :slot="cur"
        slot-scope="row"
      >
        {{ toCurrency(row.value) }}
      </template>

      <template
        v-for="dt in templateTypes.date"
        :slot="dt"
        slot-scope="row"
      >
        {{ row.value | date }}
      </template>

      <template
        v-for="(bool, idx) in templateTypes.boolean"
        :slot="bool"
        slot-scope="row"
      >
        <font-awesome-icon
          v-bind:key="`${model.type}-${model.selected}-${bool}-${idx}`"
          :class="row.value ? 'text-success' : 'text-danger'"
          :icon="row.value ? 'check' : 'times'"
        />
      </template>

      <template slot="type" slot-scope="row">
        {{
          model.type === 'liabilities' && model.selected === 'contingents' ?
            maps.contingents[row.value] :
            row.value
        }}
      </template>
    </filter-table>
    <div class="text-right mt-3 mr-3 ml-3">
      <span>Total: {{ currentTotal | currency }}</span>
    </div>
  </b-modal>
</template>

<script>
import helper from '@/libs/helper';
import FilterTable from '@/components/utils/FilterTable';

import ASSETS_MAP from '@/components/profile/sections/tabs/financial/resources/assetTypeMap';
import ASSETS_SCHEMAS from '@/components/profile/sections/tabs/financial/assets/schemas';

import LIABILITIES_MAP from '@/components/profile/sections/tabs/financial/resources/liabilityTypeMap';
import LIABILITY_SCHEMAS from '@/components/profile/sections/tabs/financial/liabilities/schemas';

import CONTINGENT_MAP from '@/components/profile/sections/tabs/financial/resources/contingentTypesMap';

import { library } from '@fortawesome/fontawesome-svg-core';
import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';

import { UPDATE_ASSETS, UPDATE_LIABILITIES } from '@/vuex/modules/profile/types/actions';


library.add(faCheck, faTimes);


export default {
  components: {
    FilterTable,
  },
  data() {
    const templateTypes = {
      currency: [
        'amount', 'cost', 'market_value', 'original_balance', 'current_balance',
        'payment_amount', 'monthly_payment', 'liability_amount', 'lien', 'surrender',
        'loan_amount', 'original_cost', 'present_value', 'holder_balance', 'holder_payment',
      ],
      date: [
        'date', 'due_date',
      ],
      boolean: [
        'installment', 'loan', 'pledged', 'delinquent',
      ],
      other: ['type'],
    };

    let templates = [];
    Object.keys(templateTypes).forEach((key) => {
      templates = templates.concat(
        Object.keys(templateTypes[key]).map(
          iKey => ({ key: templateTypes[key][iKey] }),
        ),
      );
    });

    return {
      model: this.getModel(),
      maps: {
        assets: ASSETS_MAP,
        liabilities: LIABILITIES_MAP,
        contingents: CONTINGENT_MAP,
      },
      schemas: {
        assets: ASSETS_SCHEMAS,
        liabilities: LIABILITY_SCHEMAS,
      },
      templateTypes,
      templates,
    };
  },
  computed: {
    finances() {
      return this.$store.getters.profile.finances || {};
    },
    items() {
      return this.finances[this.model.type] || {};
    },
    options() {
      return {
        type: [
          { text: 'Assets', value: 'assets' },
          { text: 'Liabilities', value: 'liabilities' },
        ],
        selected: Object.keys(this.items).map(
          key => ({ value: key, text: this.maps[this.model.type].camel[key] }),
        ),
      };
    },
    currentTotal() {
      const key = this.model.type === 'assets' && this.model.selected === 'realEstate' ?
        'present_value' : 'amount';

      const selected = this.items[this.model.selected] || [];
      const reduced = selected.reduce(
        (acc, next) => ({ [key]: parseFloat(acc[key]) + parseFloat(next[key]) }),
        { [key]: 0 },
      );

      return reduced[key];
    },
    currentFields() {
      const type = this.model.type;
      if (!type) { return []; }

      const selected = this.maps[type].camelToSnake[this.model.selected];
      const schema = this.schemas[type][selected];
      if (!schema) { return []; }

      const formatter = value => (value || '-');
      const mapToFields = form => form.fields.map(
        field => ({ key: helper.camelToSnake(field.model), sortable: true, formatter }),
      );

      if (type === 'assets' && selected === 'real_estate') {
        const propertyFields = mapToFields(schema.property.schema);
        const mortgageFields = mapToFields(schema.mortgage.schema);
        mortgageFields.forEach((field) => {
          const item = field;
          item.key = field.key.replace('.', '_');
          propertyFields.push(item);
        });
        return propertyFields;
      }

      return mapToFields(schema);
    },
    currentItems() {
      return this.items[this.model.selected] || [];
    },
  },
  methods: {
    getModel() {
      return {
        type: 'assets',
        selected: 'accountsAndNotes',
      };
    },
    toCurrency(val) {
      return val ? `$ ${helper.currency(val)}` : '-';
    },
    open(type, selected) {
      if (!type || !selected) { return; }
      this.model.type = type;
      this.model.selected = selected;
      this.$refs.assetsDetailsModal.show();
    },
    itemClicked(item, index) {
      this.$swal.fire({
        html: `
          <h4>You're about to remove this item!</h4>
          <div>Are you sure you want to continue?</div>
        `,
        type: 'warning',
        showCancelButton: true,
        reverseButtons: true,
        confirmButtonText: 'Delete',
        cancelButtonText: 'Back',
      })
        .then((result) => {
          if (!result.value) { return; }

          const userId = this.$store.getters.user.id;
          const type = this.model.type;
          const selected = this.maps[type].camelToSnake[this.model.selected];
          const url = `/users/${userId}/profile/finances/${type}/${selected}/${index}`;
          this.$reqs.delete(url)
            .then((response) => {
              const action = type === 'assets' ? UPDATE_ASSETS : UPDATE_LIABILITIES;
              this.$store.dispatch(action, response.data);
              this.$notify({ group: 'alert', text: 'Record successfully deleted.' });
            })
            .catch(() => {});
        });
    },
  },
  mounted() {
    this.$root.$on('bv::modal::hide', (bvEvent) => {
      /* Reset model when modal is hidden */
      const filterTable = this.$refs.filterTable;
      if (bvEvent.target.id === 'finances-details-modal' && filterTable) {
        filterTable.filter = null;
      }
    });
  },
};
</script>



// WEBPACK FOOTER //
// src/components/profile/sections/tabs/financial/FinancialDetailsModal.vue